/**
 * Bedienfeld des Gesprächsmodus — über dem Eingabefeld, solange gesprochen wird.
 * Zeigt, was gerade passiert (zuhören, verstehen, KIAN denkt, KIAN spricht), was verstanden
 * wurde, einen offenen Entwurf mit „Anlegen"/„Nein" und was die Sprache kostet.
 */
import { ActionIcon, Box, Button, Group, Loader, Paper, Progress, Stack, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconEar, IconHeadset, IconPlayerStop, IconVolume, IconX } from '@tabler/icons-react';
import type { useVoiceConversation, VoicePhase } from './useVoiceConversation';

type Voice = ReturnType<typeof useVoiceConversation>;

const PHASE_TEXT: Record<VoicePhase, string> = {
  off: 'Gespräch beendet',
  starting: 'Mikrofon wird vorbereitet …',
  listening: 'Ich höre zu …',
  transcribing: 'Verstehe …',
  thinking: 'KIAN denkt nach …',
  speaking: 'KIAN spricht …',
  executing: 'Wird ausgeführt …',
};

function formatCent(eur: number): string {
  const cent = eur * 100;
  return `${cent < 0.1 ? cent.toFixed(2) : cent.toFixed(1)}`.replace('.', ',');
}

export function VoiceConversationPanel({ voice }: { voice: Voice }) {
  const { phase, level, heardText, notice, pending, usage } = voice;
  if (phase === 'off' && !notice) return null;
  const busyPhase = phase === 'starting' || phase === 'transcribing' || phase === 'thinking' || phase === 'executing';
  const cost = usage.sttCostEur + usage.ttsCostEur;

  return (
    <Paper withBorder radius="md" p="xs" mx="sm" mt="xs" data-kian-voice-panel>
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            {phase === 'listening' ? (
              <IconEar size={18} color="var(--mantine-color-red-5)" />
            ) : phase === 'speaking' ? (
              <IconVolume size={18} color="var(--mantine-color-cyberOrange-5)" />
            ) : busyPhase ? (
              <Loader size={16} />
            ) : (
              <IconHeadset size={18} />
            )}
            <Text size="sm" fw={500} truncate>
              {PHASE_TEXT[phase]}
            </Text>
          </Group>
          <Group gap={4} wrap="nowrap">
            {phase === 'listening' ? (
              <Button size="compact-xs" variant="light" onClick={voice.finishTurn}>
                Fertig gesprochen
              </Button>
            ) : null}
            {phase === 'speaking' ? (
              <Button size="compact-xs" variant="light" leftSection={<IconPlayerStop size={12} />} onClick={voice.interrupt}>
                Unterbrechen
              </Button>
            ) : null}
            {phase !== 'off' ? (
              <Button size="compact-xs" color="red" variant="subtle" onClick={voice.stop}>
                Beenden
              </Button>
            ) : (
              <Tooltip label="Hinweis schließen">
                <ActionIcon size="sm" variant="subtle" color="gray" onClick={voice.clearNotice} aria-label="Hinweis schließen">
                  <IconX size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>

        {phase === 'listening' ? <Progress value={Math.round(level * 100)} size={4} color="red" transitionDuration={80} /> : null}

        {heardText && phase !== 'off' ? (
          <Text size="xs" c="dimmed" lineClamp={2}>
            Du: „{heardText}"
          </Text>
        ) : null}

        {pending ? (
          <Box
            p={6}
            style={{ borderRadius: 'var(--mantine-radius-sm)', background: 'var(--mantine-color-yellow-light)' }}
          >
            <Group justify="space-between" gap="xs" wrap="wrap">
              <Text size="xs">
                Sag „Ja, {pending.verb}" oder „Nein" — oder tippe:
              </Text>
              <Group gap={4}>
                <Button size="compact-xs" color="cyberOrange" leftSection={<IconCheck size={12} />} onClick={voice.confirmDraft}>
                  {pending.verb === 'anlegen' ? 'Anlegen' : 'Ausführen'}
                </Button>
                <Button size="compact-xs" variant="default" onClick={voice.rejectDraft}>
                  Nein
                </Button>
              </Group>
            </Group>
          </Box>
        ) : null}

        {notice ? (
          <Text size="xs" c={phase === 'off' ? 'red' : 'yellow'}>
            {notice}
          </Text>
        ) : null}

        {usage.heard > 0 || usage.spokenChars > 0 ? (
          <Text size="xs" c="dimmed">
            Sprache: {usage.heard}× gehört · {usage.spokenChars.toLocaleString('de-DE')} Zeichen vorgelesen ·{' '}
            {usage.costIncomplete ? 'mind. ' : '≈ '}
            {formatCent(cost)} ct
          </Text>
        ) : null}
      </Stack>
    </Paper>
  );
}
